import { useEffect, useState } from 'react';
import { sprintf, __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { usePaymentInputs } from './hooks';
import { PaymentInputsWrapper, TermsCheckbox } from './components';
import images from './images';
import { PAYMENT_STORE_KEY } from '@woocommerce/block-data';
import { subscribe, select, dispatch } from '@wordpress/data';
import { extensionCartUpdate } from '@woocommerce/blocks-checkout';

const settings = getSetting( 'wc_valorpay_data', {} );
if ( settings && ! settings.is_not_blocked ) {
	dispatch( 'core/notices' ).createErrorNotice(
		__(
			'Valor Pay is disabled due to multiple payment failures.',
			'wc-valorpay'
		),
		{
			context: 'wc/checkout/payments', // Display the notice in the payments context.
		}
	);
}
if ( settings && settings.card_type_allowed ) {
	let noticeMsg = null;
	if ( 'debit' === settings.card_type_allowed ) {
		noticeMsg = __( 'Only debit card allowed', 'wc-valorpay' );
	}
	if ( 'credit' === settings.card_type_allowed ) {
		noticeMsg = __( 'Only credit card allowed', 'wc-valorpay' );
	}
	if ( noticeMsg ) {
		dispatch( 'core/notices' ).createInfoNotice( noticeMsg, {
			context: 'wc/checkout/payments', // Display the notice in the payments context.
		} );
	}
}
subscribe( function () {
	const paymentMethodState = select( PAYMENT_STORE_KEY );
	const chosenPaymentMethod = paymentMethodState.getActivePaymentMethod();
	const savedPaymentMethods = paymentMethodState.getSavedPaymentMethods();
	const selectedPaymentTokenId = +paymentMethodState.getActiveSavedToken();
	let selectedCardType = '';
	if ( selectedPaymentTokenId ) {
		const foundMethod = savedPaymentMethods.cc.find(
			( method ) =>
				method.tokenId === selectedPaymentTokenId &&
				method.method.gateway === 'wc_valorpay'
		);
		if ( foundMethod ) {
			selectedCardType = foundMethod.method.card_type;
		}
	}

	extensionCartUpdate( {
		namespace: 'wc-valorpay-fee-update',
		data: {
			action_type: 'update_payment',
			payment_method: chosenPaymentMethod,
			card_type: selectedCardType,
		},
	} );
}, PAYMENT_STORE_KEY );

const defaultLabel = __( 'Valor Pay', 'wc-valorpay' );

const label = decodeEntities( settings.title ) || defaultLabel;
/**
 * Content component
 */
const Content = ( props ) => {
	const [ isFetchingCardType, setIsFetchingCardType ] = useState( false );
	const [ isInitPay, setIsInitPay ] = useState( false );
	let paymentFields = [ 'cardNumber', 'expiryDate', 'cvc' ];
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'zip'
	) {
		paymentFields.push( 'zip' );
	}
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'address'
	) {
		paymentFields.push( 'streetaddress' );
	}
	const {
		getCardNumberProps,
		getExpiryDateProps,
		getCVCProps,
		getCardImageProps,
		getZIPProps,
		getStreetAddressProps,
		wrapperProps,
		meta: {
			erroredInputs,
			isTouched,
			cardNumberField,
			expiryDateField,
			cvcField,
			zipField,
			addressField,
		},
	} = usePaymentInputs( {
		avsType: settings.avs_type,
		setIsFetchingCardType,
		paymentFields: paymentFields,
	} );
	const {
		components: { LoadingMask },
		eventRegistration,
		emitResponse,
		paymentStatus,
	} = props;
	const [ isTermsChecked, setIsTermsChecked ] = useState( true );
	const { onPaymentSetup, onCheckoutAfterProcessingWithError } =
		eventRegistration;
	const handleCheckboxChange = () => {
		setIsTermsChecked((prev) => !prev);
	};	
	useEffect( () => {
		const unsubscribe = onPaymentSetup( async () => {
			let isValid = true;
			for ( let errorInput in erroredInputs ) {
				if ( erroredInputs[ errorInput ] ) {
					isValid = false;
					break;
				}
			}
			if ( isValid ) {
				let paymentMethodData = {
					'wc_valorpay-card-number': cardNumberField.current.value,
					'wc_valorpay-card-expiry': expiryDateField.current.value,
					'wc_valorpay-card-cvc': cvcField.current.value,
					valorpay_avs_type: settings.avs_type,
					valorpay_terms: isTermsChecked,
				};
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'zip'
				) {
					paymentMethodData.valorpay_avs_zip = zipField.current.value;
				}
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'address'
				) {
					paymentMethodData.valorpay_avs_street =
						addressField.current.value;
				}
				return {
					type: emitResponse.responseTypes.SUCCESS,
					meta: {
						paymentMethodData: {
							...paymentMethodData,
						},
					},
				};
			}
			setIsInitPay( true );
			return {
				type: emitResponse.responseTypes.ERROR,
				message: __(
					'Please provide payment information',
					'wc-valorpay'
				),
			};
		} );

		const unsubscribeErrorMsg = onCheckoutAfterProcessingWithError(
			( { processingResponse } ) => {
				if ( processingResponse?.paymentDetails?.errorMessage ) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message: processingResponse.paymentDetails.errorMessage,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				}
				// so we don't break the observers.
				return true;
			}
		);
		// Unsubscribes when this component is unmounted.
		return () => {
			unsubscribe();
			unsubscribeErrorMsg();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		onPaymentSetup,
		erroredInputs,
		onCheckoutAfterProcessingWithError,
	] );
	return (
		<>
			{ settings.is_sandbox_mode && (
				<p>
					{ __(
						'TEST MODE ENABLED. Use test card number 4111111111111111 with 999 as CVC and a future expiration date.',
						'wc-valorpay'
					) }
				</p>
			) }
			<LoadingMask
				showSpinner={ isFetchingCardType }
				isLoading={ isFetchingCardType }
				screenReaderLabel={ __(
					'Fetching Card Type...',
					'wc-valorpay'
				) }
			>
				<PaymentInputsWrapper
					{ ...wrapperProps }
					isInitPay={ isInitPay }
				>
					<svg { ...getCardImageProps( { images } ) } />
					<input { ...getCardNumberProps() } />
					<input { ...getExpiryDateProps() } />
					<input { ...getCVCProps() } />

					{ ( settings.avs_type === 'zipandaddress' ||
						settings.avs_type === 'zip' ) && (
						<input { ...getZIPProps() } />
					) }
					{ ( settings.avs_type === 'zipandaddress' ||
						settings.avs_type === 'address' ) && (
						<input { ...getStreetAddressProps() } />
					) }
				</PaymentInputsWrapper>
			</LoadingMask>
			{settings.is_terms_conditions_enable && <TermsCheckbox
				id="valorpay-terms-new-card"
				checked={ isTermsChecked }
				onChange={ handleCheckboxChange }
				label={
					<span
						dangerouslySetInnerHTML={ {
							__html: settings.terms_label,
						} }
					/>
				}
			/>}
		</>
	);
};

const SavedToken = ( props ) => {
	const [ isInitPay, setIsInitPay ] = useState( false );
	let paymentFields = [];
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'zip'
	) {
		paymentFields.push( 'zip' );
	}
	if (
		settings.avs_type === 'zipandaddress' ||
		settings.avs_type === 'address'
	) {
		paymentFields.push( 'streetaddress' );
	}
	const {
		getZIPProps,
		getStreetAddressProps,
		wrapperProps,
		meta: { erroredInputs, zipField, addressField },
	} = usePaymentInputs( {
		avsType: settings.avs_type,
		paymentFields: paymentFields,
	} );
	const { eventRegistration, emitResponse, paymentStatus, token } = props;
	const { onPaymentSetup, onCheckoutAfterProcessingWithError } =
		eventRegistration;
	const [ isTermsChecked, setIsTermsChecked ] = useState( true );
	const handleCheckboxChange = () => {
		setIsTermsChecked((prev) => !prev);
	};	
	useEffect( () => {
		const unsubscribe = onPaymentSetup( async () => {
			let isValid = true;
			for ( let errorInput in erroredInputs ) {
				if ( erroredInputs[ errorInput ] ) {
					isValid = false;
					break;
				}
			}
			if ( isValid ) {
				let paymentMethodData = {
					'wc-wc_valorpay-payment-token': token,
					valorpay_avs_type: settings.avs_type,
					valorpay_terms: isTermsChecked,
				};
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'zip'
				) {
					paymentMethodData.valorpay_avs_zip = zipField.current.value;
				}
				if (
					settings.avs_type === 'zipandaddress' ||
					settings.avs_type === 'address'
				) {
					paymentMethodData.valorpay_avs_street =
						addressField.current.value;
				}
				return {
					type: emitResponse.responseTypes.SUCCESS,
					meta: {
						paymentMethodData: {
							...paymentMethodData,
						},
					},
				};
			}
			setIsInitPay( true );
			return {
				type: emitResponse.responseTypes.ERROR,
				message: __(
					'Please provide payment information',
					'wc-valorpay'
				),
			};
		} );
		const unsubscribeErrorMsg = onCheckoutAfterProcessingWithError(
			( { processingResponse } ) => {
				if ( processingResponse?.paymentDetails?.errorMessage ) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message: processingResponse.paymentDetails.errorMessage,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				}
				// so we don't break the observers.
				return true;
			}
		);
		// Unsubscribes when this component is unmounted.
		return () => {
			unsubscribe();
			unsubscribeErrorMsg();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		onPaymentSetup,
		erroredInputs,
		token,
	] );
	return (
		<>
			{ settings.avs_type !== 'none' && (
				<>
					<PaymentInputsWrapper
						{ ...wrapperProps }
						isInitPay={ isInitPay }
					>
						{ ( settings.avs_type === 'zipandaddress' ||
							settings.avs_type === 'zip' ) && (
							<input { ...getZIPProps() } />
						) }
						{ ( settings.avs_type === 'zipandaddress' ||
							settings.avs_type === 'address' ) && (
							<input { ...getStreetAddressProps() } />
						) }
					</PaymentInputsWrapper>
				</>
			) }
			{settings.is_terms_conditions_enable && <TermsCheckbox
				id="valorpay-terms-saved-token"
				checked={ isTermsChecked }
				onChange={ handleCheckboxChange }
				label={
					<span
						dangerouslySetInnerHTML={ {
							__html: settings.terms_label,
						} }
					/>
				}
			/>}
		</>
	);
};

/**
 * Label component
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel, PaymentMethodIcons } = props.components;
	return (
		<>
			<PaymentMethodLabel text={ label } />
			<PaymentMethodIcons icons={ settings.card_types } align="right" />
		</>
	);
};

/**
 * ValorPay payment method config object.
 */
const ValorPay = {
	name: 'wc_valorpay',
	label: <Label />,
	gatewayId: 'wc_valorpay',
	content: <Content />,
	edit: <Content />,
	canMakePayment: () => settings.is_not_blocked,
	savedTokenComponent: <SavedToken />,
	ariaLabel: label,
	supports: {
		features: settings.supports,
		showSaveOption: true,
	},
};

registerPaymentMethod( ValorPay );
